import React from 'react';
import { Trans } from '@lingui/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import EXPERIMENTS from '@rover/constants/src/platform/experiments.constants';
import { Box } from '@rover/kibble/core';
import MarketingOptIn from '@rover/react-lib/src/components/modals/MarketingOptIn';
import HomePageSignals from '@rover/react-lib/src/components/RoverProtectSignals/HomePageSignals';
import {
  HOMEPAGE_REVIEWS,
  HOMEPAGE_ROVERPROTECT,
} from '@rover/react-lib/src/components/RoverProtectSignals/utils/RoverProtectSignals.constants';
import ScrollToTopOnMount from '@rover/react-lib/src/components/utils/ScrollToTopOnMount';
import Footer from '@rover/react-lib/src/pages/shared/FooterLinks/Footer';
import { BrandLocalityServiceCitiesItem } from '@rover/rsdk/src/apiClient/latest/model/brandLocalityServiceCitiesItem';
import type { CountryFooterLink } from '@rover/rsdk/src/apiClient/latest/model/countryFooterLink';
import { OptimizedSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResponse';
import type { PaginatedCountryRegionLinkList } from '@rover/rsdk/src/apiClient/latest/model/paginatedCountryRegionLinkList';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import useExperimentsShim from '@rover/shared/js/hooks/useExperimentsShim';
import useFlag from '@rover/shared/js/hooks/useFlag';
import type { HomepageMetaTags } from '@rover/types/src/HomepageConfiguration';

import AppStoreCallout from '../../shared/AppStoreCallout/AppStoreCallout';
import { HomePageLayout } from '../common/styles';

import HomePageFaqs from './components/HomePageFaqs';
import HomePageHero from './components/HomePageHero';
import HomepageSitterCarousel from './components/HomepageSitterCarousel';
import RoverTestimonials from './components/RoverTestimonials';
import SeparatorIcon from './components/separator.svg';
import ServicesForEveryPet from './components/ServicesForEveryPet';
import HtmlHeader from './HtmlHeader';

type Props = {
  citiesAndNeighborhoodsLinks?: BrandLocalityServiceCitiesItem[];
  countryFooterLinks?: CountryFooterLink[];
  countryRegionLinks: PaginatedCountryRegionLinkList;
  roverGuaranteeLink?: string;
  metaTags: HomepageMetaTags;
  cxOperatingHours?: string;
  sitterCarouselResultsResponse?: OptimizedSearchResponse;
};

const DefaultBrandHomePageTemplate = ({
  metaTags: { title, metaDescription },
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
  roverGuaranteeLink,
  cxOperatingHours,
  sitterCarouselResultsResponse,
}: Props): JSX.Element => {
  const { isVariant } = useExperimentsShim();
  const isSimplifiedExperience = isVariant(EXPERIMENTS.SIMPLIFIED_HOMEPAGE_SEARCH_FORM_EXPERIMENT);
  const sitterCarouselEnabled = useFlag(FLAGS.ROLLOUT_SITTER_CAROUSEL);

  const showSitterCarousel =
    sitterCarouselEnabled && isSimplifiedExperience && !!sitterCarouselResultsResponse;
  return (
    <>
      <HtmlHeader title={title} metaDescription={metaDescription} />
      <ScrollToTopOnMount />
      <HomePageHero />
      <MarketingOptIn />
      <HomePageLayout>
        <ServicesForEveryPet />
        {showSitterCarousel && (
          <Box width="100%">
            <Box display="flex" justifyContent="center" mt={['8x', '0x']} mb={['4x', '12x']}>
              <SeparatorIcon />
            </Box>
            <HomepageSitterCarousel sitterCarouselResultsResponse={sitterCarouselResultsResponse} />
          </Box>
        )}
        <HomePageSignals />
        <RoverTestimonials />
      </HomePageLayout>

      <HowBrandWorks py={['0x', '8x']} mt={['8x', '8x', '0x']}>
        <HowBrandWorks.Display>
          <Trans>Meet local sitters who will treat your pets like family</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>
              <Trans>Search</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>{HOMEPAGE_REVIEWS}</HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>
              <Trans>Book & pay</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                No cash or checks needed—we make it simple to book and make secured payments through
                our website or app.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>
              <Trans>Relax</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>{HOMEPAGE_ROVERPROTECT}</HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      {roverGuaranteeLink && cxOperatingHours && (
        <HomePageFaqs
          mt={['16x', '24x']}
          pb={['16x', '24x']}
          roverGuaranteeLink={roverGuaranteeLink}
          cxOperatingHours={cxOperatingHours}
        />
      )}
      <HomePageLayout>
        <AppStoreCallout />
      </HomePageLayout>
      <Footer
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
      />
    </>
  );
};

export default DefaultBrandHomePageTemplate;
